import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from '../locales/en/common.json';
import enWorkspace from '../locales/en/workspace.json';
import enPlan from '../locales/en/plan.json';
import enChat from '../locales/en/chat.json';
import enReport from '../locales/en/report.json';
import enAuditing from '../locales/en/auditing.json';
import enToolbox from '../locales/en/toolbox.json';
import enErrors from '../locales/en/errors.json';
import enNav from '../locales/en/nav.json';
import enAnalytics from '../locales/en/analytics.json';
import enCatalog from '../locales/en/catalog.json';
import enNotifications from '../locales/en/notifications.json';
import enProgress from '../locales/en/progress.json';
import enReggie from '../locales/en/reggie.json';
import enTerms from '../locales/en/terms.json';

import frCommon from '../locales/fr/common.json';
import frWorkspace from '../locales/fr/workspace.json';
import frPlan from '../locales/fr/plan.json';
import frChat from '../locales/fr/chat.json';
import frReport from '../locales/fr/report.json';
import frAuditing from '../locales/fr/auditing.json';
import frToolbox from '../locales/fr/toolbox.json';
import frErrors from '../locales/fr/errors.json';
import frNav from '../locales/fr/nav.json';
import frAnalytics from '../locales/fr/analytics.json';
import frCatalog from '../locales/fr/catalog.json';
import frNotifications from '../locales/fr/notifications.json';
import frProgress from '../locales/fr/progress.json';
import frReggie from '../locales/fr/reggie.json';
import frTerms from '../locales/fr/terms.json';

import zhCNCommon from '../locales/zh-CN/common.json';
import zhCNWorkspace from '../locales/zh-CN/workspace.json';
import zhCNPlan from '../locales/zh-CN/plan.json';
import zhCNChat from '../locales/zh-CN/chat.json';
import zhCNReport from '../locales/zh-CN/report.json';
import zhCNAuditing from '../locales/zh-CN/auditing.json';
import zhCNToolbox from '../locales/zh-CN/toolbox.json';
import zhCNErrors from '../locales/zh-CN/errors.json';
import zhCNNav from '../locales/zh-CN/nav.json';
import zhCNAnalytics from '../locales/zh-CN/analytics.json';
import zhCNCatalog from '../locales/zh-CN/catalog.json';
import zhCNNotifications from '../locales/zh-CN/notifications.json';
import zhCNProgress from '../locales/zh-CN/progress.json';
import zhCNReggie from '../locales/zh-CN/reggie.json';
import zhCNTerms from '../locales/zh-CN/terms.json';

// i18n init with feature-scoped namespaces.
//
// Architecture
// - en is the baseline; fr + zh-CN are additional supported locales.
//   * zh-CN canonicalized per ADR-041 (BCP-47 4-char form, mixed case).
// - Strings live under src/locales/{en,fr,zh-CN}/{namespace}.json.
// - Picker selection persists to localStorage under key `bennie.ui.lang`. On first
//   load we fall back to `navigator.language` (first segment) before defaulting to en.
// - fallback chain: missing key → fall through to en (i18next default per fallbackLng).
//
// Locale-code resolution (per ADR-041 A1):
// - Exact match against SUPPORTED_LANGUAGES first (so `zh-CN` resolves exactly,
//   not collapsing to `zh` then to `en`).
// - Then `split('-')[0]` collapse for 2-char codes (so `fr-CA`/`fr-FR` collapse to `fr`).
// - Bare `zh` is NOT in the allow-list — collapses to en (per A1.a strict-reject).
// - `zh-TW`, `zh-Hans`, `zh-HK` also collapse to `zh` → en (per A1.b — never silently
//   substitute zh-CN for a different Chinese variant).
//
// Workspace.output_language is a SEPARATE concern from UI language. The picker drives
// UI language only. workspace.output_language is an explicit, editable user choice in
// the create modal whose CREATE-TIME DEFAULT is seeded from the active UI locale
// (currentUiLanguage() — en|fr|zh-CN) so an operator already working in a non-English
// UI sees the report language pre-selected to match; it remains operator-overridable
// and is immutable once the workspace is created. Mid-workspace UI-language switching
// does NOT change an existing workspace's report-output language.

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'zh-CN'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const STORAGE_KEY = 'bennie.ui.lang';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        workspace: enWorkspace,
        plan: enPlan,
        chat: enChat,
        report: enReport,
        // Cycle 5 auditing rework (WI-11) — the auditing workspace's four tabs and run blocks.
        auditing: enAuditing,
        toolbox: enToolbox,
        errors: enErrors,
        nav: enNav,
        analytics: enAnalytics,
        catalog: enCatalog,
        notifications: enNotifications,
        progress: enProgress,
        reggie: enReggie,
        terms: enTerms,
      },
      fr: {
        common: frCommon,
        workspace: frWorkspace,
        plan: frPlan,
        chat: frChat,
        report: frReport,
        auditing: frAuditing,
        toolbox: frToolbox,
        errors: frErrors,
        nav: frNav,
        analytics: frAnalytics,
        catalog: frCatalog,
        notifications: frNotifications,
        progress: frProgress,
        reggie: frReggie,
        terms: frTerms,
      },
      'zh-CN': {
        common: zhCNCommon,
        workspace: zhCNWorkspace,
        plan: zhCNPlan,
        chat: zhCNChat,
        report: zhCNReport,
        auditing: zhCNAuditing,
        toolbox: zhCNToolbox,
        errors: zhCNErrors,
        nav: zhCNNav,
        analytics: zhCNAnalytics,
        catalog: zhCNCatalog,
        notifications: zhCNNotifications,
        progress: zhCNProgress,
        reggie: zhCNReggie,
        terms: zhCNTerms,
      },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    // load: 'currentOnly' prevents i18next from walking the parent-language
    // resolve hierarchy. Without it, 'zh-CN' would resolve to ['zh-CN', 'zh',
    // 'en'] and i18next would silently pick 'zh' (no resources) before falling
    // back to 'en' — surfacing as "selecting zh-CN keeps showing English."
    // 'fr-CA' / 'en-US' canonicalization happens at detector boundary via
    // convertDetectedLanguage (see detection.convertDetectedLanguage below).
    load: 'currentOnly' as const,
    defaultNS: 'common',
    ns: ['common', 'workspace', 'plan', 'chat', 'report', 'toolbox', 'errors', 'nav', 'analytics', 'catalog', 'notifications', 'progress', 'reggie', 'auditing', 'terms'],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
      // Canonicalize raw detected codes BEFORE the cache write so localStorage
      // never persists a non-canonical form ('en-US' → 'en'; 'zh-CN' → 'zh-CN';
      // 'zh-TW' → 'en' fallback per A1.b). Without this, the LanguageDetector
      // collapses 'en-US' → 'en' via nonExplicitSupportedLngs but still WRITES
      // 'en-US' to localStorage, which then overrode any prior user selection
      // on the next page load. Surfaced as F-RUN-W10-2 in WS-ZH-A walk.
      convertDetectedLanguage: (lng: string): string => {
        const lower = (lng ?? '').toLowerCase();
        for (const canonical of SUPPORTED_LANGUAGES) {
          if (canonical.toLowerCase() === lower) return canonical;
        }
        const head = lower.split('-')[0];
        for (const canonical of SUPPORTED_LANGUAGES) {
          if (canonical.toLowerCase() === head) return canonical;
        }
        return 'en';
      },
    },
    returnNull: false,
  });

export default i18n;

/**
 * Read the current UI language as one of our supported codes. Used when sending
 * output_language to the API at workspace creation. Defaults to 'en' if i18n
 * hasn't initialized yet or if the resolved language isn't in our supported set.
 *
 * Per ADR-041 A1: exact-match the full code first (so `zh-CN` resolves to itself,
 * not collapsing to `zh` then to `en`). Fall through to `split('-')[0]` collapse
 * for sub-tags of 2-char canonical codes (`fr-CA` → `fr`, `en-GB` → `en`).
 */
export function currentUiLanguage(): SupportedLanguage {
  const raw = i18n.resolvedLanguage ?? i18n.language ?? 'en';

  // Exact-match against the canonical set (case-insensitive). Preserves the
  // canonical casing of the matched entry (e.g., input 'zh-cn' → 'zh-CN').
  const rawLower = raw.toLowerCase();
  for (const canonical of SUPPORTED_LANGUAGES) {
    if (canonical.toLowerCase() === rawLower) return canonical;
  }

  // 2-char collapse: 'fr-CA' → 'fr'; 'en-GB' → 'en'; 'zh-TW' → 'zh' (not in
  // SUPPORTED_LANGUAGES, falls to en). Never silently maps non-CN Chinese
  // variants to zh-CN.
  const head = rawLower.split('-')[0];
  for (const canonical of SUPPORTED_LANGUAGES) {
    if (canonical.toLowerCase() === head) return canonical;
  }

  return 'en';
}
